<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          Payday Accrual
        </oxd-text>
        <oxd-switch-input
          v-model="configuration.enabled"
          label-position="left"
          option-label="Enable Payday Accrual"
        />
      </div>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSubmit">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.hoursPerDay"
                label="Hours Per Day"
                :rules="rules.hoursPerDay"
                required
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                Used to convert leave entitlement days into hours for payroll
                accrual math. For example, 8 hours equals 1 day.
              </oxd-text>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Payday accrual uses 24 semi-monthly pay periods with anchor
                dates on the 15th and the last day of each month.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                If an anchor date falls on a weekend or holiday, OrangeHRM rolls
                it back to the nearest prior business day before accruing.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Job title overrides take priority over employment status
                overrides, service milestones stack after the employee reaches
                each anniversary, and benefit package extras use the employee's
                Pay Grade.
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text tag="p" class="orangehrm-optional-field-label">
                Leave Types
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Choose which leave types should accrue across pay periods and
                then layer on rules for staff, directors, anniversaries, and
                benefit packages.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Leave types can optionally stop accruing when the employee's
                available balance reaches a limit, such as Sick stopping at 8
                days until some leave is used.
              </oxd-text>

              <div class="orangehrm-leave-type-list">
                <div
                  v-if="availableLeaveTypes.length === 0"
                  class="orangehrm-leave-type-empty"
                >
                  No active leave types available.
                </div>

                <div
                  v-for="leaveType in availableLeaveTypes"
                  :key="leaveType.id"
                  class="orangehrm-leave-type-row"
                >
                  <div class="orangehrm-leave-type-header">
                    <oxd-text tag="p" class="orangehrm-leave-type-label">
                      {{ leaveType.name }}
                    </oxd-text>
                    <oxd-switch-input
                      :model-value="isLeaveTypeEnabled(leaveType.id)"
                      @update:model-value="
                        setLeaveTypeEnabled(leaveType.id, $event)
                      "
                    />
                  </div>

                  <oxd-grid :cols="2" class="orangehrm-full-width-grid">
                    <oxd-grid-item>
                      <oxd-input-field
                        :model-value="getBaseAnnualDays(leaveType.id)"
                        label="Base Annual Days"
                        :rules="rules.optionalDecimal"
                        :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                        placeholder="Leave blank to keep the existing annual entitlement"
                        @update:model-value="
                          setBaseAnnualDays(leaveType.id, $event)
                        "
                      />
                      <oxd-text tag="p" class="orangehrm-input-hint">
                        Default annual days before staff/director overrides,
                        anniversaries, and benefit package extras are applied.
                      </oxd-text>
                    </oxd-grid-item>
                    <oxd-grid-item>
                      <oxd-input-field
                        :model-value="getBalanceCapDays(leaveType.id)"
                        label="Available Balance Cap (Days)"
                        :rules="rules.optionalDecimal"
                        :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                        placeholder="Leave blank for no cap"
                        @update:model-value="
                          setBalanceCapDays(leaveType.id, $event)
                        "
                      />
                      <oxd-text tag="p" class="orangehrm-input-hint">
                        If set, future payday accrual pauses after the
                        employee's available balance reaches this amount.
                      </oxd-text>
                    </oxd-grid-item>
                  </oxd-grid>

                  <div class="orangehrm-rule-section">
                    <div class="orangehrm-rule-section-header">
                      <div>
                        <oxd-text tag="p" class="orangehrm-rule-section-title">
                          January 1 Front-Load
                        </oxd-text>
                        <oxd-text tag="p" class="orangehrm-input-hint">
                          Give the full annual entitlement on January 1 so the
                          employee can use the whole bucket up front and then
                          earn it back across the 24 pay periods.
                        </oxd-text>
                        <oxd-text tag="p" class="orangehrm-input-hint">
                          This is useful for borrowed-time vacation plans where
                          the employee may owe time back if they leave before
                          the borrowed balance returns to zero.
                        </oxd-text>
                      </div>
                      <oxd-switch-input
                        :model-value="isFrontLoadEnabled(leaveType.id)"
                        :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                        @update:model-value="
                          setFrontLoadEnabled(leaveType.id, $event)
                        "
                      />
                    </div>
                  </div>

                  <div class="orangehrm-rule-section">
                    <div class="orangehrm-rule-section-header">
                      <div>
                        <oxd-text tag="p" class="orangehrm-rule-section-title">
                          Employment Status Overrides
                        </oxd-text>
                        <oxd-text tag="p" class="orangehrm-input-hint">
                          Use this for rules like Full-Time staff starting at 10
                          vacation days.
                        </oxd-text>
                      </div>
                      <oxd-button
                        type="button"
                        display-type="text"
                        label="Add Override"
                        :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                        @click="addEmploymentStatusRule(leaveType.id)"
                      />
                    </div>

                    <div
                      v-if="getEmploymentStatusRules(leaveType.id).length === 0"
                      class="orangehrm-rule-empty"
                    >
                      No employment status overrides.
                    </div>

                    <div
                      v-for="(rule, index) in getEmploymentStatusRules(
                        leaveType.id,
                      )"
                      :key="`${leaveType.id}-employment-status-${index}`"
                      class="orangehrm-rule-row"
                    >
                      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
                        <oxd-grid-item>
                          <oxd-input-field
                            v-model="rule.employmentStatusId"
                            type="select"
                            label="Employment Status"
                            :options="availableEmploymentStatuses"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                          />
                        </oxd-grid-item>
                        <oxd-grid-item>
                          <oxd-input-field
                            v-model="rule.annualDays"
                            label="Annual Days"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                          />
                        </oxd-grid-item>
                        <oxd-grid-item class="orangehrm-rule-row-action">
                          <oxd-button
                            type="button"
                            display-type="text"
                            label="Remove"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                            @click="
                              removeEmploymentStatusRule(leaveType.id, index)
                            "
                          />
                        </oxd-grid-item>
                      </oxd-grid>
                    </div>
                  </div>

                  <div class="orangehrm-rule-section">
                    <div class="orangehrm-rule-section-header">
                      <div>
                        <oxd-text tag="p" class="orangehrm-rule-section-title">
                          Job Title Overrides
                        </oxd-text>
                        <oxd-text tag="p" class="orangehrm-input-hint">
                          Use this for rules like Director starting at 15
                          vacation days. These override employment status days.
                        </oxd-text>
                      </div>
                      <oxd-button
                        type="button"
                        display-type="text"
                        label="Add Override"
                        :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                        @click="addJobTitleRule(leaveType.id)"
                      />
                    </div>

                    <div
                      v-if="getJobTitleRules(leaveType.id).length === 0"
                      class="orangehrm-rule-empty"
                    >
                      No job title overrides.
                    </div>

                    <div
                      v-for="(rule, index) in getJobTitleRules(leaveType.id)"
                      :key="`${leaveType.id}-job-title-${index}`"
                      class="orangehrm-rule-row"
                    >
                      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
                        <oxd-grid-item>
                          <oxd-input-field
                            v-model="rule.jobTitleId"
                            type="select"
                            label="Job Title"
                            :options="availableJobTitles"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                          />
                        </oxd-grid-item>
                        <oxd-grid-item>
                          <oxd-input-field
                            v-model="rule.annualDays"
                            label="Annual Days"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                          />
                        </oxd-grid-item>
                        <oxd-grid-item class="orangehrm-rule-row-action">
                          <oxd-button
                            type="button"
                            display-type="text"
                            label="Remove"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                            @click="removeJobTitleRule(leaveType.id, index)"
                          />
                        </oxd-grid-item>
                      </oxd-grid>
                    </div>
                  </div>

                  <div class="orangehrm-rule-section">
                    <div class="orangehrm-rule-section-header">
                      <div>
                        <oxd-text tag="p" class="orangehrm-rule-section-title">
                          Service Milestones
                        </oxd-text>
                        <oxd-text tag="p" class="orangehrm-input-hint">
                          Add extra annual days after specific anniversaries.
                          These extras stack once the employee reaches each
                          milestone.
                        </oxd-text>
                      </div>
                      <oxd-button
                        type="button"
                        display-type="text"
                        label="Add Milestone"
                        :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                        @click="addServiceMilestoneRule(leaveType.id)"
                      />
                    </div>

                    <div
                      v-if="getServiceMilestoneRules(leaveType.id).length === 0"
                      class="orangehrm-rule-empty"
                    >
                      No service milestones.
                    </div>

                    <div
                      v-for="(rule, index) in getServiceMilestoneRules(
                        leaveType.id,
                      )"
                      :key="`${leaveType.id}-service-milestone-${index}`"
                      class="orangehrm-rule-row"
                    >
                      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
                        <oxd-grid-item>
                          <oxd-input-field
                            v-model="rule.yearsOfService"
                            label="Years of Service"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                          />
                        </oxd-grid-item>
                        <oxd-grid-item>
                          <oxd-input-field
                            v-model="rule.extraDays"
                            label="Extra Annual Days"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                          />
                        </oxd-grid-item>
                        <oxd-grid-item class="orangehrm-rule-row-action">
                          <oxd-button
                            type="button"
                            display-type="text"
                            label="Remove"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                            @click="
                              removeServiceMilestoneRule(leaveType.id, index)
                            "
                          />
                        </oxd-grid-item>
                      </oxd-grid>
                    </div>
                  </div>

                  <div class="orangehrm-rule-section">
                    <div class="orangehrm-rule-section-header">
                      <div>
                        <oxd-text tag="p" class="orangehrm-rule-section-title">
                          Benefit Package Extra Hours
                        </oxd-text>
                        <oxd-text tag="p" class="orangehrm-input-hint">
                          Benefit package extras are mapped to Pay Grades so you
                          can add annual hours based on the employee's package.
                        </oxd-text>
                      </div>
                      <oxd-button
                        type="button"
                        display-type="text"
                        label="Add Package"
                        :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                        @click="addPayGradeRule(leaveType.id)"
                      />
                    </div>

                    <div
                      v-if="getPayGradeRules(leaveType.id).length === 0"
                      class="orangehrm-rule-empty"
                    >
                      No benefit package extras.
                    </div>

                    <div
                      v-for="(rule, index) in getPayGradeRules(leaveType.id)"
                      :key="`${leaveType.id}-pay-grade-${index}`"
                      class="orangehrm-rule-row"
                    >
                      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
                        <oxd-grid-item>
                          <oxd-input-field
                            v-model="rule.payGradeId"
                            type="select"
                            label="Pay Grade"
                            :options="availablePayGrades"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                          />
                        </oxd-grid-item>
                        <oxd-grid-item>
                          <oxd-input-field
                            v-model="rule.extraHours"
                            label="Extra Annual Hours"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                          />
                        </oxd-grid-item>
                        <oxd-grid-item class="orangehrm-rule-row-action">
                          <oxd-button
                            type="button"
                            display-type="text"
                            label="Remove"
                            :disabled="isLeaveTypeRuleDisabled(leaveType.id)"
                            @click="removePayGradeRule(leaveType.id, index)"
                          />
                        </oxd-grid-item>
                      </oxd-grid>
                    </div>
                  </div>
                </div>
              </div>

              <oxd-text
                v-if="
                  configuration.enabled &&
                  configuration.leaveTypeIds.length === 0
                "
                tag="p"
                class="orangehrm-validation-error"
              >
                Select at least one leave type before enabling payday accrual.
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text tag="p" class="orangehrm-optional-field-label">
                Front-Load Benefits
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Use these buttons to apply the current front-loaded leave rules
                right away. OrangeHRM uses today's date, so employees keep the
                full annual bucket while the borrowed portion reflects what they
                have not earned back yet.
              </oxd-text>
              <oxd-input-field
                v-model="frontLoadEmployees"
                type="autocomplete"
                label="Selected Staff Members"
                :multiple="true"
                :disabled="!canRunFrontLoadActions"
                :clear="false"
                :create-options="loadFrontLoadEmployees"
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                Leave this list empty if you want to front-load everyone with an
                eligible front-loaded leave type, or pick specific staff members
                before using the selected-staff button.
              </oxd-text>
              <oxd-text
                v-if="!hasConfiguredFrontLoadLeaveTypes"
                tag="p"
                class="orangehrm-input-hint orangehrm-front-load-disabled-note"
              >
                Turn on `January 1 Front-Load` for at least one enabled leave
                type before running the front-load action.
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Preview initialization of current-year balances with
                <code
                  >php bin/console orangehrm:leave:payday-accrual --initialize
                  --dry-run</code
                >.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Then run
                <code>php bin/console orangehrm:leave:payday-accrual</code>
                from cron on business days.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Initialization recalculates balances using the configured
                accrual mode and annual targets, including existing plans.
                Review its preview before applying changes to already credited
                balances.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                From January 1, 2027, mapped handbook leave types use the
                monthly rules in Leave → 2027 Handbook Policy. This payday
                process skips those types to prevent duplicate credits.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Balance caps pause future payday accrual once the current
                available balance reaches the configured limit in incremental
                mode; they do not retroactively accrue skipped capped periods
                later.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Front-loaded leave types keep the full annual bucket visible all
                year, while each effective payday reduces the borrowed portion
                the employee would still owe back on a final paycheck.
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <div class="orangehrm-actions-group">
            <oxd-button
              type="button"
              display-type="ghost"
              :label="$t('general.reset')"
              @click="onReset"
            />
            <oxd-button
              type="submit"
              display-type="secondary"
              label="Front-Load Everyone"
              :disabled="!canRunFrontLoadActions"
              @click="setSubmitAction('frontLoadAll')"
            />
            <oxd-button
              type="submit"
              display-type="secondary"
              label="Front-Load Selected Staff"
              :disabled="!canRunFrontLoadSelected"
              @click="setSubmitAction('frontLoadSelected')"
            />
            <oxd-button
              type="submit"
              display-type="secondary"
              :label="$t('general.save')"
              @click="setSubmitAction('save')"
            />
          </div>
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  digitsOnlyWithDecimalPoint,
  required,
  shouldNotExceedCharLength,
} from '@ohrm/core/util/validation/rules';
import {OxdSwitchInput} from '@ohrm/oxd';

const emptyRuleSet = () => ({
  baseAnnualDays: '',
  frontLoadOnJanuaryFirst: false,
  employmentStatusAnnualDays: [],
  jobTitleAnnualDays: [],
  serviceMilestones: [],
  payGradeExtraHours: [],
});

const defaultConfiguration = () => ({
  enabled: false,
  hoursPerDay: '8',
  leaveTypeIds: [],
  balanceCaps: {},
  ruleSets: {},
});

const cloneRuleSet = (ruleSet = emptyRuleSet()) => ({
  baseAnnualDays: ruleSet.baseAnnualDays ?? '',
  frontLoadOnJanuaryFirst: Boolean(ruleSet.frontLoadOnJanuaryFirst),
  employmentStatusAnnualDays: [
    ...(ruleSet.employmentStatusAnnualDays ?? []),
  ].map((rule) => ({
    employmentStatusId: rule.employmentStatusId ?? null,
    annualDays: rule.annualDays ?? '',
  })),
  jobTitleAnnualDays: [...(ruleSet.jobTitleAnnualDays ?? [])].map((rule) => ({
    jobTitleId: rule.jobTitleId ?? null,
    annualDays: rule.annualDays ?? '',
  })),
  serviceMilestones: [...(ruleSet.serviceMilestones ?? [])].map((rule) => ({
    yearsOfService: rule.yearsOfService ?? '',
    extraDays: rule.extraDays ?? '',
  })),
  payGradeExtraHours: [...(ruleSet.payGradeExtraHours ?? [])].map((rule) => ({
    payGradeId: rule.payGradeId ?? null,
    extraHours: rule.extraHours ?? '',
  })),
});

const cloneConfiguration = (configuration) => ({
  ...configuration,
  leaveTypeIds: [...(configuration.leaveTypeIds ?? [])],
  balanceCaps: {
    ...(configuration.balanceCaps ?? {}),
  },
  ruleSets: Object.entries(configuration.ruleSets ?? {}).reduce(
    (clonedRuleSets, [leaveTypeId, ruleSet]) => ({
      ...clonedRuleSets,
      [leaveTypeId]: cloneRuleSet(ruleSet),
    }),
    {},
  ),
});

const normalizeDecimal = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value)
    .replace(/(\.\d*?[1-9])0+$/u, '$1')
    .replace(/\.0+$/u, '')
    .replace(/\.$/u, '');
};

const positiveDecimal = (value) => {
  if (!value) {
    return true;
  }

  return parseFloat(value) > 0 || 'Enter a value greater than zero.';
};

export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/payday-accrual-configuration',
    );
    const employeeHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/employees',
    );

    return {
      http,
      employeeHttp,
    };
  },

  data() {
    return {
      isLoading: false,
      frontLoadEmployees: [],
      configuration: defaultConfiguration(),
      initialConfiguration: defaultConfiguration(),
      availableLeaveTypes: [],
      availableEmploymentStatuses: [],
      availableJobTitles: [],
      availablePayGrades: [],
      submitAction: 'save',
      rules: {
        hoursPerDay: [
          required,
          shouldNotExceedCharLength(20),
          digitsOnlyWithDecimalPoint,
          positiveDecimal,
        ],
        optionalDecimal: [
          shouldNotExceedCharLength(20),
          digitsOnlyWithDecimalPoint,
          positiveDecimal,
        ],
      },
    };
  },

  computed: {
    hasConfiguredFrontLoadLeaveTypes() {
      return this.configuration.leaveTypeIds.some((leaveTypeId) =>
        Boolean(
          this.configuration.ruleSets[String(leaveTypeId)]
            ?.frontLoadOnJanuaryFirst,
        ),
      );
    },
    canRunFrontLoadActions() {
      return (
        this.configuration.enabled && this.hasConfiguredFrontLoadLeaveTypes
      );
    },
    canRunFrontLoadSelected() {
      return (
        this.canRunFrontLoadActions &&
        this.frontLoadEmployees.length > 0 &&
        this.frontLoadEmployees.every((employee) =>
          Number.isFinite(Number(employee?.id)),
        )
      );
    },
  },

  created() {
    this.loadConfiguration();
  },

  methods: {
    async loadFrontLoadEmployees(searchParam) {
      return new Promise((resolve) => {
        if (searchParam.trim() && searchParam.length < 100) {
          this.employeeHttp
            .getAll({
              nameOrId: searchParam.trim(),
            })
            .then(({data}) => {
              resolve(
                data.data.map((employee) => ({
                  id: employee.empNumber,
                  label:
                    `${employee.firstName} ${employee.middleName} ${employee.lastName}`.trim(),
                  _employee: employee,
                  isPastEmployee: !!employee.terminationId,
                })),
              );
            });
        } else {
          resolve([]);
        }
      });
    },

    extractResponseData(data) {
      const {
        availableLeaveTypes = [],
        availableEmploymentStatuses = [],
        availableJobTitles = [],
        availablePayGrades = [],
        ...configuration
      } = data ?? {};

      return {
        availableLeaveTypes,
        availableEmploymentStatuses,
        availableJobTitles,
        availablePayGrades,
        configuration,
      };
    },

    getSelectedOptionId(optionOrId) {
      if (optionOrId && typeof optionOrId === 'object') {
        return Number(optionOrId.id);
      }

      return Number(optionOrId);
    },

    findOptionById(options, optionId) {
      const normalizedOptionId = Number(optionId);
      if (!Number.isFinite(normalizedOptionId)) {
        return null;
      }

      return (
        options.find((option) => Number(option.id) === normalizedOptionId) ??
        null
      );
    },

    normalizeConfiguration(configuration) {
      return {
        ...defaultConfiguration(),
        ...configuration,
        hoursPerDay: normalizeDecimal(configuration.hoursPerDay || '8'),
        leaveTypeIds: [...(configuration.leaveTypeIds ?? [])].sort(
          (leftId, rightId) => leftId - rightId,
        ),
        balanceCaps: this.normalizeBalanceCaps(configuration.balanceCaps),
        ruleSets: this.normalizeRuleSets(configuration.ruleSets),
      };
    },

    normalizeBalanceCaps(balanceCaps) {
      if (!balanceCaps || typeof balanceCaps !== 'object') {
        return {};
      }

      return Object.entries(balanceCaps).reduce(
        (normalizedBalanceCaps, [leaveTypeId, balanceCap]) => {
          const normalizedLeaveTypeId = Number(leaveTypeId);
          const normalizedBalanceCap = normalizeDecimal(balanceCap);

          if (
            !Number.isFinite(normalizedLeaveTypeId) ||
            normalizedBalanceCap === ''
          ) {
            return normalizedBalanceCaps;
          }

          normalizedBalanceCaps[String(normalizedLeaveTypeId)] =
            normalizedBalanceCap;
          return normalizedBalanceCaps;
        },
        {},
      );
    },

    normalizeRuleSets(ruleSets) {
      if (!ruleSets || typeof ruleSets !== 'object') {
        return {};
      }

      return Object.entries(ruleSets).reduce(
        (normalizedRuleSets, [leaveTypeId, ruleSet]) => {
          const normalizedLeaveTypeId = Number(leaveTypeId);
          if (!Number.isFinite(normalizedLeaveTypeId) || !ruleSet) {
            return normalizedRuleSets;
          }

          const normalizedRuleSet = cloneRuleSet({
            baseAnnualDays: normalizeDecimal(ruleSet.baseAnnualDays),
            frontLoadOnJanuaryFirst: Boolean(ruleSet.frontLoadOnJanuaryFirst),
            employmentStatusAnnualDays: Object.entries(
              ruleSet.employmentStatusAnnualDays ?? {},
            ).map(([employmentStatusId, annualDays]) => ({
              employmentStatusId: this.findOptionById(
                this.availableEmploymentStatuses,
                employmentStatusId,
              ),
              annualDays: normalizeDecimal(annualDays),
            })),
            jobTitleAnnualDays: Object.entries(
              ruleSet.jobTitleAnnualDays ?? {},
            ).map(([jobTitleId, annualDays]) => ({
              jobTitleId: this.findOptionById(
                this.availableJobTitles,
                jobTitleId,
              ),
              annualDays: normalizeDecimal(annualDays),
            })),
            serviceMilestones: [...(ruleSet.serviceMilestones ?? [])].map(
              (serviceMilestone) => ({
                yearsOfService: String(serviceMilestone.yearsOfService ?? ''),
                extraDays: normalizeDecimal(serviceMilestone.extraDays),
              }),
            ),
            payGradeExtraHours: Object.entries(
              ruleSet.payGradeExtraHours ?? {},
            ).map(([payGradeId, extraHours]) => ({
              payGradeId: this.findOptionById(
                this.availablePayGrades,
                payGradeId,
              ),
              extraHours: normalizeDecimal(extraHours),
            })),
          });

          normalizedRuleSets[String(normalizedLeaveTypeId)] = normalizedRuleSet;
          return normalizedRuleSets;
        },
        {},
      );
    },

    loadConfiguration() {
      this.isLoading = true;
      this.http
        .getAll()
        .then((response) => {
          const {
            configuration,
            availableLeaveTypes,
            availableEmploymentStatuses,
            availableJobTitles,
            availablePayGrades,
          } = this.extractResponseData(response.data.data);
          this.availableLeaveTypes = availableLeaveTypes;
          this.availableEmploymentStatuses = availableEmploymentStatuses;
          this.availableJobTitles = availableJobTitles;
          this.availablePayGrades = availablePayGrades;
          const normalizedConfiguration =
            this.normalizeConfiguration(configuration);
          this.configuration = cloneConfiguration(normalizedConfiguration);
          this.initialConfiguration = cloneConfiguration(
            normalizedConfiguration,
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    isLeaveTypeEnabled(leaveTypeId) {
      return this.configuration.leaveTypeIds.includes(leaveTypeId);
    },

    isLeaveTypeRuleDisabled(leaveTypeId) {
      return (
        !this.configuration.enabled || !this.isLeaveTypeEnabled(leaveTypeId)
      );
    },

    setLeaveTypeEnabled(leaveTypeId, isEnabled) {
      const leaveTypeIds = new Set(this.configuration.leaveTypeIds);
      if (isEnabled) {
        leaveTypeIds.add(leaveTypeId);
      } else {
        leaveTypeIds.delete(leaveTypeId);
      }

      this.configuration.leaveTypeIds = Array.from(leaveTypeIds).sort(
        (leftId, rightId) => leftId - rightId,
      );
    },

    getBalanceCapDays(leaveTypeId) {
      return this.configuration.balanceCaps[String(leaveTypeId)] ?? '';
    },

    setBalanceCapDays(leaveTypeId, value) {
      const normalizedValue = normalizeDecimal(value);
      const balanceCaps = {
        ...this.configuration.balanceCaps,
      };

      if (normalizedValue === '') {
        delete balanceCaps[String(leaveTypeId)];
      } else {
        balanceCaps[String(leaveTypeId)] = normalizedValue;
      }

      this.configuration.balanceCaps = balanceCaps;
    },

    getRuleSet(leaveTypeId) {
      return this.configuration.ruleSets[String(leaveTypeId)] ?? emptyRuleSet();
    },

    ensureRuleSet(leaveTypeId) {
      const leaveTypeKey = String(leaveTypeId);
      if (!this.configuration.ruleSets[leaveTypeKey]) {
        this.configuration.ruleSets = {
          ...this.configuration.ruleSets,
          [leaveTypeKey]: emptyRuleSet(),
        };
      }

      return this.configuration.ruleSets[leaveTypeKey];
    },

    cleanupRuleSet(leaveTypeId) {
      const leaveTypeKey = String(leaveTypeId);
      const ruleSet = this.configuration.ruleSets[leaveTypeKey];
      if (!ruleSet) {
        return;
      }

      const hasContent =
        normalizeDecimal(ruleSet.baseAnnualDays) !== '' ||
        Boolean(ruleSet.frontLoadOnJanuaryFirst) ||
        ruleSet.employmentStatusAnnualDays.length > 0 ||
        ruleSet.jobTitleAnnualDays.length > 0 ||
        ruleSet.serviceMilestones.length > 0 ||
        ruleSet.payGradeExtraHours.length > 0;

      if (!hasContent) {
        const ruleSets = {
          ...this.configuration.ruleSets,
        };
        delete ruleSets[leaveTypeKey];
        this.configuration.ruleSets = ruleSets;
      }
    },

    getBaseAnnualDays(leaveTypeId) {
      return this.getRuleSet(leaveTypeId).baseAnnualDays ?? '';
    },

    setBaseAnnualDays(leaveTypeId, value) {
      const normalizedValue = normalizeDecimal(value);
      if (normalizedValue === '') {
        if (this.configuration.ruleSets[String(leaveTypeId)]) {
          this.configuration.ruleSets[String(leaveTypeId)].baseAnnualDays = '';
          this.cleanupRuleSet(leaveTypeId);
        }
        return;
      }

      this.ensureRuleSet(leaveTypeId).baseAnnualDays = normalizedValue;
    },

    isFrontLoadEnabled(leaveTypeId) {
      return Boolean(this.getRuleSet(leaveTypeId).frontLoadOnJanuaryFirst);
    },

    setFrontLoadEnabled(leaveTypeId, value) {
      this.ensureRuleSet(leaveTypeId).frontLoadOnJanuaryFirst = Boolean(value);
      if (!value) {
        this.cleanupRuleSet(leaveTypeId);
      }
    },

    getEmploymentStatusRules(leaveTypeId) {
      return this.getRuleSet(leaveTypeId).employmentStatusAnnualDays ?? [];
    },

    addEmploymentStatusRule(leaveTypeId) {
      this.ensureRuleSet(leaveTypeId).employmentStatusAnnualDays.push({
        employmentStatusId: null,
        annualDays: '',
      });
    },

    removeEmploymentStatusRule(leaveTypeId, index) {
      const ruleSet = this.configuration.ruleSets[String(leaveTypeId)];
      if (!ruleSet) {
        return;
      }

      ruleSet.employmentStatusAnnualDays.splice(index, 1);
      this.cleanupRuleSet(leaveTypeId);
    },

    getJobTitleRules(leaveTypeId) {
      return this.getRuleSet(leaveTypeId).jobTitleAnnualDays ?? [];
    },

    addJobTitleRule(leaveTypeId) {
      this.ensureRuleSet(leaveTypeId).jobTitleAnnualDays.push({
        jobTitleId: null,
        annualDays: '',
      });
    },

    removeJobTitleRule(leaveTypeId, index) {
      const ruleSet = this.configuration.ruleSets[String(leaveTypeId)];
      if (!ruleSet) {
        return;
      }

      ruleSet.jobTitleAnnualDays.splice(index, 1);
      this.cleanupRuleSet(leaveTypeId);
    },

    getServiceMilestoneRules(leaveTypeId) {
      return this.getRuleSet(leaveTypeId).serviceMilestones ?? [];
    },

    addServiceMilestoneRule(leaveTypeId) {
      this.ensureRuleSet(leaveTypeId).serviceMilestones.push({
        yearsOfService: '',
        extraDays: '',
      });
    },

    removeServiceMilestoneRule(leaveTypeId, index) {
      const ruleSet = this.configuration.ruleSets[String(leaveTypeId)];
      if (!ruleSet) {
        return;
      }

      ruleSet.serviceMilestones.splice(index, 1);
      this.cleanupRuleSet(leaveTypeId);
    },

    getPayGradeRules(leaveTypeId) {
      return this.getRuleSet(leaveTypeId).payGradeExtraHours ?? [];
    },

    addPayGradeRule(leaveTypeId) {
      this.ensureRuleSet(leaveTypeId).payGradeExtraHours.push({
        payGradeId: null,
        extraHours: '',
      });
    },

    removePayGradeRule(leaveTypeId, index) {
      const ruleSet = this.configuration.ruleSets[String(leaveTypeId)];
      if (!ruleSet) {
        return;
      }

      ruleSet.payGradeExtraHours.splice(index, 1);
      this.cleanupRuleSet(leaveTypeId);
    },

    serializeRuleSet(ruleSet) {
      const baseAnnualDays = normalizeDecimal(ruleSet.baseAnnualDays);
      const employmentStatusAnnualDays = (
        ruleSet.employmentStatusAnnualDays ?? []
      )
        .filter(
          (rule) =>
            Number.isFinite(
              this.getSelectedOptionId(rule.employmentStatusId),
            ) && normalizeDecimal(rule.annualDays) !== '',
        )
        .reduce(
          (mapping, rule) => ({
            ...mapping,
            [String(this.getSelectedOptionId(rule.employmentStatusId))]:
              normalizeDecimal(rule.annualDays),
          }),
          {},
        );
      const jobTitleAnnualDays = (ruleSet.jobTitleAnnualDays ?? [])
        .filter(
          (rule) =>
            Number.isFinite(this.getSelectedOptionId(rule.jobTitleId)) &&
            normalizeDecimal(rule.annualDays) !== '',
        )
        .reduce(
          (mapping, rule) => ({
            ...mapping,
            [String(this.getSelectedOptionId(rule.jobTitleId))]:
              normalizeDecimal(rule.annualDays),
          }),
          {},
        );
      const serviceMilestones = (ruleSet.serviceMilestones ?? [])
        .filter(
          (rule) =>
            /^\d+$/u.test(String(rule.yearsOfService ?? '')) &&
            Number(rule.yearsOfService) > 0 &&
            normalizeDecimal(rule.extraDays) !== '',
        )
        .map((rule) => ({
          yearsOfService: Number(rule.yearsOfService),
          extraDays: normalizeDecimal(rule.extraDays),
        }));
      const payGradeExtraHours = (ruleSet.payGradeExtraHours ?? [])
        .filter(
          (rule) =>
            Number.isFinite(this.getSelectedOptionId(rule.payGradeId)) &&
            normalizeDecimal(rule.extraHours) !== '',
        )
        .reduce(
          (mapping, rule) => ({
            ...mapping,
            [String(this.getSelectedOptionId(rule.payGradeId))]:
              normalizeDecimal(rule.extraHours),
          }),
          {},
        );

      const serializedRuleSet = {};
      if (baseAnnualDays !== '') {
        serializedRuleSet.baseAnnualDays = baseAnnualDays;
      }
      if (ruleSet.frontLoadOnJanuaryFirst) {
        serializedRuleSet.frontLoadOnJanuaryFirst = true;
      }
      if (Object.keys(employmentStatusAnnualDays).length > 0) {
        serializedRuleSet.employmentStatusAnnualDays =
          employmentStatusAnnualDays;
      }
      if (Object.keys(jobTitleAnnualDays).length > 0) {
        serializedRuleSet.jobTitleAnnualDays = jobTitleAnnualDays;
      }
      if (serviceMilestones.length > 0) {
        serializedRuleSet.serviceMilestones = serviceMilestones;
      }
      if (Object.keys(payGradeExtraHours).length > 0) {
        serializedRuleSet.payGradeExtraHours = payGradeExtraHours;
      }

      return Object.keys(serializedRuleSet).length > 0
        ? serializedRuleSet
        : null;
    },

    serializeRuleSets(ruleSets) {
      return Object.entries(ruleSets ?? {}).reduce(
        (serializedRuleSets, [leaveTypeId, ruleSet]) => {
          const serializedRuleSet = this.serializeRuleSet(ruleSet);
          if (!serializedRuleSet) {
            return serializedRuleSets;
          }

          return {
            ...serializedRuleSets,
            [leaveTypeId]: serializedRuleSet,
          };
        },
        {},
      );
    },

    onReset() {
      this.configuration = cloneConfiguration(this.initialConfiguration);
      this.frontLoadEmployees = [];
      this.submitAction = 'save';
    },

    setSubmitAction(action) {
      this.submitAction = action;
    },

    getFrontLoadEmployeeIds() {
      return this.frontLoadEmployees
        .map((employee) => Number(employee?.id))
        .filter((empNumber) => Number.isFinite(empNumber));
    },

    onSubmit() {
      if (
        this.configuration.enabled &&
        this.configuration.leaveTypeIds.length === 0
      ) {
        this.$toast.warn({
          title: this.$t('general.invalid'),
          message: 'Select at least one leave type to enable payday accrual.',
        });
        return;
      }

      if (
        this.submitAction !== 'save' &&
        !this.hasConfiguredFrontLoadLeaveTypes
      ) {
        this.$toast.warn({
          title: this.$t('general.invalid'),
          message:
            'Enable January 1 Front-Load for at least one enabled leave type before running this action.',
        });
        this.submitAction = 'save';
        return;
      }

      if (
        this.submitAction === 'frontLoadSelected' &&
        this.getFrontLoadEmployeeIds().length === 0
      ) {
        this.$toast.warn({
          title: this.$t('general.invalid'),
          message: 'Select at least one staff member to front-load.',
        });
        this.submitAction = 'save';
        return;
      }

      this.isLoading = true;
      const isFrontLoadAction =
        this.submitAction === 'frontLoadAll' ||
        this.submitAction === 'frontLoadSelected';
      this.http
        .request({
          method: 'PUT',
          data: {
            action: isFrontLoadAction ? 'frontLoad' : 'save',
            enabled: this.configuration.enabled,
            hoursPerDay: this.configuration.hoursPerDay,
            leaveTypeIds: this.configuration.leaveTypeIds,
            balanceCaps: this.configuration.balanceCaps,
            ruleSets: this.serializeRuleSets(this.configuration.ruleSets),
            empNumbers:
              this.submitAction === 'frontLoadSelected'
                ? this.getFrontLoadEmployeeIds()
                : [],
          },
        })
        .then((response) => {
          const {
            configuration,
            availableLeaveTypes,
            availableEmploymentStatuses,
            availableJobTitles,
            availablePayGrades,
          } = this.extractResponseData(response.data.data);
          this.availableLeaveTypes = availableLeaveTypes;
          this.availableEmploymentStatuses = availableEmploymentStatuses;
          this.availableJobTitles = availableJobTitles;
          this.availablePayGrades = availablePayGrades;
          const normalizedConfiguration =
            this.normalizeConfiguration(configuration);
          this.configuration = cloneConfiguration(normalizedConfiguration);
          this.initialConfiguration = cloneConfiguration(
            normalizedConfiguration,
          );

          if (isFrontLoadAction) {
            const processedCount =
              response.data.meta?.frontLoadProcessedCount ?? 0;
            const skippedCount = response.data.meta?.frontLoadSkippedCount ?? 0;

            this.frontLoadEmployees = [];

            if (processedCount > 0) {
              return this.$toast.success({
                title: this.$t('general.success'),
                message:
                  skippedCount > 0
                    ? `Front-loaded ${processedCount} leave entitlement row(s). ${skippedCount} row(s) were skipped.`
                    : `Front-loaded ${processedCount} leave entitlement row(s).`,
              });
            }

            return this.$toast.info({
              title: this.$t('general.info'),
              message:
                skippedCount > 0
                  ? `No new front-loads were applied. ${skippedCount} row(s) were skipped.`
                  : 'No eligible front-loaded leave entitlements matched this request.',
            });
          }

          return this.$toast.updateSuccess();
        })
        .finally(() => {
          this.isLoading = false;
          this.submitAction = 'save';
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-actions-group {
  display: flex;
  gap: 1rem;
}

.orangehrm-leave-type-list {
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  border-radius: 0.75rem;
  overflow: hidden;
}

.orangehrm-leave-type-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid $oxd-interface-gray-lighten-2-color;
  background: linear-gradient(
    180deg,
    rgba(23, 49, 64, 0.015),
    rgba(23, 49, 64, 0)
  );
}

.orangehrm-leave-type-row:last-child {
  border-bottom: none;
}

.orangehrm-leave-type-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.orangehrm-leave-type-label {
  margin-right: 1rem;
}

.orangehrm-leave-type-empty {
  padding: 1rem;
  color: $oxd-interface-gray-color;
}

.orangehrm-rule-section {
  padding: 1rem;
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  border-radius: 0.75rem;
  background-color: rgba(255, 255, 255, 0.88);
}

.orangehrm-rule-section + .orangehrm-rule-section {
  margin-top: 0.25rem;
}

.orangehrm-rule-section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.orangehrm-rule-section-title {
  margin-bottom: 0.2rem;
  font-weight: 700;
}

.orangehrm-rule-empty {
  color: $oxd-interface-gray-color;
  font-size: 0.95rem;
}

.orangehrm-rule-row + .orangehrm-rule-row {
  margin-top: 0.75rem;
}

.orangehrm-rule-row-action {
  display: flex;
  align-items: flex-end;
}

.orangehrm-validation-error {
  color: $oxd-feedback-danger-color;
  margin-top: 0.75rem;
}

.orangehrm-front-load-disabled-note {
  color: $oxd-interface-gray-darken-1-color;
  margin-top: 0.5rem;
}
</style>
